import { useContext, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid';
import { ReactComponent as SmashLogo } from '../../assets/smash_logo.svg';
import DarkModeToggle from './DarkModeToggle';
import { UserContext } from '../../utils/contexts/UserContext';

const sectionLinks = [
  { label: 'Platform', href: '/#platform' },
  { label: 'How it works', href: '/#journey' },
  { label: 'Human review', href: '/#trust' },
  { label: 'IB Schools', href: '/ib-schools' },
];

export default function PublicNavBar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const auth = useContext(UserContext) || {};
  const dashboardPath = auth.isStudent
    ? '/portfolio'
    : auth.isExpert
      ? '/expert/reviews'
      : auth.isUniversity
        ? '/organization/dashboard'
        : auth.isAdmin
          ? '/platform'
          : '/profile';

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" onClick={closeMenu} className="rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-950" aria-label="Smash home">
            <SmashLogo className="h-auto w-24" />
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Public navigation">
            {sectionLinks.map((item) => (
              <a key={item.href} href={item.href} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-blue-300">
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            <DarkModeToggle />
            {auth.isLoggedIn ? <Link to={dashboardPath} className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-600 dark:hover:bg-blue-500 dark:focus:ring-offset-slate-950">Open dashboard</Link> : <><Link to="/login" className={`rounded-lg px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${location.pathname === '/login' ? 'bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-200' : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'}`}>Log in</Link><Link to="/register" className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-600 dark:hover:bg-blue-500 dark:focus:ring-offset-slate-950">Create account</Link></>}
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <DarkModeToggle />
            <button type="button" onClick={() => setIsMenuOpen((open) => !open)} className="rounded-lg p-2 text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-slate-200 dark:hover:bg-slate-800" aria-expanded={isMenuOpen} aria-controls="public-mobile-menu">
              <span className="sr-only">{isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}</span>
              {isMenuOpen ? <XMarkIcon className="h-6 w-6" aria-hidden="true" /> : <Bars3Icon className="h-6 w-6" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <nav id="public-mobile-menu" className="border-t border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-950 md:hidden" aria-label="Mobile public navigation">
            <div className="mx-auto flex max-w-7xl flex-col gap-2">
              {sectionLinks.map((item) => <a key={item.href} href={item.href} onClick={closeMenu} className="rounded-lg px-3 py-3 font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800">{item.label}</a>)}
              <div className="mt-2 grid grid-cols-2 gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
                {auth.isLoggedIn ? <Link to={dashboardPath} onClick={closeMenu} className="col-span-2 rounded-lg bg-blue-700 px-4 py-2.5 text-center font-semibold text-white">Open dashboard</Link> : <><Link to="/login" onClick={closeMenu} className="rounded-lg border border-slate-300 px-4 py-2.5 text-center font-semibold text-slate-800 dark:border-slate-700 dark:text-white">Log in</Link><Link to="/register" onClick={closeMenu} className="rounded-lg bg-blue-700 px-4 py-2.5 text-center font-semibold text-white">Create account</Link></>}
              </div>
            </div>
          </nav>
        )}
      </header>

      <Outlet />

      <footer className="border-t border-slate-200 bg-white px-6 py-8 text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 text-sm sm:flex-row">
          <p>&copy; {new Date().getFullYear()} Smash Index. All rights reserved.</p>
          <div className="flex gap-5"><a href="#terms" className="hover:text-blue-700 dark:hover:text-blue-300">Terms</a><a href="#privacy" className="hover:text-blue-700 dark:hover:text-blue-300">Privacy</a></div>
        </div>
      </footer>
    </div>
  );
}
